import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Terminal, Lock, Download, ChevronLeft, ChevronRight, LogOut, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

function ErrorNote({ testId, message }) {
  if (!message) return null;
  return (
    <p data-testid={testId} className="mb-4 mt-3 flex items-center gap-2 text-sm text-danger">
      <AlertTriangle className="h-4 w-4" />
      {message}
    </p>
  );
}

function TokenGate({ error, onUnlock }) {
  const [tokenInput, setTokenInput] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const value = tokenInput.trim();
    if (!value) return;
    setTokenInput("");
    onUnlock(value);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-6 font-mono text-[#EAEAEA]">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-white/10 bg-black/40 p-8"
      >
        <div className="mb-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash">
          <Lock className="h-4 w-4 text-neon" />
          comms://admin
        </div>
        <label htmlFor="admin-token" className="mb-2 block text-xs uppercase tracking-[0.2em] text-ash">
          Admin token
        </label>
        <input
          id="admin-token"
          type="password"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          data-testid="admin-token-input"
          className="w-full border border-white/15 bg-black/40 px-4 py-3 text-sm text-[#EAEAEA] focus:border-neon focus:outline-none"
          autoFocus
        />
        <ErrorNote testId="admin-login-error" message={error} />
        <button
          type="submit"
          data-testid="admin-login-submit"
          className="mt-6 w-full border border-neon px-4 py-3 text-sm font-bold uppercase tracking-[0.2em] text-neon transition-colors duration-200 hover:bg-neon hover:text-black"
        >
          Unlock
        </button>
      </form>
    </div>
  );
}

function AdminHeader({ onLogout }) {
  return (
    <div className="mb-8 flex items-center justify-between border-b border-white/10 pb-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash">
        <Terminal className="h-4 w-4 text-neon" />
        comms://admin/subscribers
      </div>
      <button
        onClick={onLogout}
        data-testid="admin-logout-button"
        className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-crimson"
      >
        <LogOut className="h-3.5 w-3.5" />
        Lock
      </button>
    </div>
  );
}

function SubscriberTable({ status, subscribers }) {
  return (
    <div className="border border-white/10">
      <table className="w-full text-left text-sm" data-testid="admin-subscribers-table">
        <thead>
          <tr className="border-b border-white/10 text-xs uppercase tracking-[0.2em] text-ash">
            <th className="px-4 py-3 font-normal">Email</th>
            <th className="px-4 py-3 font-normal">Subscribed at</th>
          </tr>
        </thead>
        <tbody>
          {status === "loading" && (
            <tr>
              <td colSpan={2} className="px-4 py-6 text-center text-ash">
                Loading...
              </td>
            </tr>
          )}
          {status === "ready" && subscribers.length === 0 && (
            <tr>
              <td colSpan={2} className="px-4 py-6 text-center text-ash">
                No subscribers yet.
              </td>
            </tr>
          )}
          {subscribers.map((s) => (
            <tr key={s.email} className="border-b border-white/5 last:border-0">
              <td className="px-4 py-3">{s.email}</td>
              <td className="px-4 py-3 text-ash">{s.subscribed_at}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ skip, total, onPageChange }) {
  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pageCount <= 1) return null;
  return (
    <div className="mt-6 flex items-center justify-center gap-4 text-xs uppercase tracking-[0.2em] text-ash">
      <button
        onClick={() => onPageChange(Math.max(0, skip - PAGE_SIZE))}
        disabled={skip === 0}
        data-testid="admin-prev-page"
        className="flex items-center gap-1 disabled:opacity-30"
      >
        <ChevronLeft className="h-4 w-4" />
        Prev
      </button>
      <span>
        Page {page} / {pageCount}
      </span>
      <button
        onClick={() => onPageChange(skip + PAGE_SIZE)}
        disabled={page >= pageCount}
        data-testid="admin-next-page"
        className="flex items-center gap-1 disabled:opacity-30"
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const load = useCallback(async (activeToken, activeSkip) => {
    setStatus("loading");
    setError("");
    try {
      const res = await axios.get(`${API}/admin/subscribers`, {
        headers: { "X-Admin-Token": activeToken },
        params: { limit: PAGE_SIZE, skip: activeSkip },
      });
      setSubscribers(res.data.subscribers);
      setTotal(res.data.total);
      setStatus("ready");
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 503) {
        setToken("");
        setError(err?.response?.data?.detail || "Access denied.");
      } else {
        setError("Failed to load subscribers — try again.");
      }
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (token) load(token, skip);
  }, [token, skip, load]);

  const unlock = (value) => {
    setToken(value);
    setSkip(0);
  };

  const logout = () => {
    setToken("");
    setSubscribers([]);
    setTotal(0);
    setSkip(0);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers/export`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "subscribers.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Export failed — try again.");
    }
  };

  if (!token) return <TokenGate error={error} onUnlock={unlock} />;

  return (
    <div className="min-h-screen bg-void px-6 py-10 font-mono text-[#EAEAEA] sm:px-12">
      <div className="mx-auto max-w-4xl">
        <AdminHeader onLogout={logout} />

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight">
            Subscribers <span className="text-neon">({total})</span>
          </h1>
          <button
            onClick={exportCsv}
            disabled={total === 0}
            data-testid="admin-export-button"
            className="flex items-center gap-2 border border-crimson px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-crimson transition-colors duration-200 hover:bg-crimson hover:text-black disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>

        <ErrorNote testId="admin-error" message={error} />
        <SubscriberTable status={status} subscribers={subscribers} />
        <Pagination skip={skip} total={total} onPageChange={setSkip} />
      </div>
    </div>
  );
}
